var _AsyncChildObservableClass_procedure;
import { __classPrivateFieldGet, __classPrivateFieldSet } from "tslib";
import { IList, MutableSet, pipe } from '@noshiro/ts-utils';
import { isManagerObservable } from '../types';
import { binarySearch, issueToken, maxDepth } from '../utils';
import { ObservableBaseClass } from './observable-base-class';
const registerChild = (child, parents) => {
    for (const p of parents) {
        p.addChild(child);
    }
    // register child to all reachable ManagerObservables
    const mut_rest = pipe(parents).chain(IList.from).chain(IList.asMut).value;
    while (mut_rest.length >= 1) {
        const p = mut_rest.pop();
        if (p === undefined)
            break;
        if (isManagerObservable(p)) {
            p.addDescendant(child);
        }
        else {
            // trace back dependency graph
            mut_rest.push(...p.parents);
        }
    }
};
const tryComplete = ({ hasSubscriber, hasActiveChild, parents, complete, }) => {
    // If there is no working parent node
    if (parents.every((r) => r.isCompleted)) {
        complete();
        return;
    }
    // If there are no active child node
    if (!hasSubscriber && !hasActiveChild) {
        complete();
    }
    // propagate to parents
    for (const par of parents) {
        par.tryComplete();
    }
};
export class AsyncChildObservableClass extends ObservableBaseClass {
    constructor({ type, parents, depth = 1 + maxDepth(parents), currentValueInit, }) {
        super({
            kind: 'async child',
            type,
            depth,
            currentValueInit,
        });
        _AsyncChildObservableClass_procedure.set(this, void 0);
        this.type = type;
        this.parents = parents;
        __classPrivateFieldSet(this, _AsyncChildObservableClass_procedure, [], "f");
        this._descendantsIdSet = new MutableSet();
        registerChild(this, parents);
    }
    // overload
    addDescendant(child) {
        if (this._descendantsIdSet.has(child.id))
            return;
        this._descendantsIdSet.add(child.id);
        const insertPos = binarySearch(__classPrivateFieldGet(this, _AsyncChildObservableClass_procedure, "f").map((a) => a.depth), child.depth);
        __classPrivateFieldSet(this, _AsyncChildObservableClass_procedure, IList.insert(__classPrivateFieldGet(this, _AsyncChildObservableClass_procedure, "f"), insertPos, child), "f");
    }
    startUpdate(nextValue) {
        const token = issueToken();
        this.setNext(nextValue, token);
        for (const p of __classPrivateFieldGet(this, _AsyncChildObservableClass_procedure, "f")) {
            p.tryUpdate(token);
        }
    }
    complete() {
        super.complete();
        // propagate to parents
        for (const par of this.parents) {
            par.tryComplete();
        }
    }
    tryComplete() {
        tryComplete({
            complete: () => {
                this.complete();
            },
            hasActiveChild: this.hasActiveChild(),
            hasSubscriber: this.hasSubscriber,
            parents: this.parents,
        });
    }
}
_AsyncChildObservableClass_procedure = new WeakMap();
export class SyncChildObservableClass extends ObservableBaseClass {
    constructor({ type, parents, depth = 1 + maxDepth(parents), currentValueInit, }) {
        super({
            kind: 'sync child',
            type,
            depth,
            currentValueInit,
        });
        this.type = type;
        this.parents = parents;
        registerChild(this, parents);
    }
    complete() {
        super.complete();
        for (const par of this.parents) {
            par.tryComplete();
        }
    }
    tryComplete() {
        tryComplete({
            complete: () => {
                this.complete();
            },
            hasActiveChild: this.hasActiveChild(),
            hasSubscriber: this.hasSubscriber,
            parents: this.parents,
        });
    }
}
export class InitializedSyncChildObservableClass extends SyncChildObservableClass {
    constructor({ type, parents, depth = 1 + maxDepth(parents), currentValueInit, }) {
        super({ type, parents, depth, currentValueInit });
    }
    get currentValue() {
        return super.getCurrentValue();
    }
    chain(operator) {
        return operator(this);
    }
}
